import anime from 'animejs';
import Link from 'next/link';
import type { HTMLAttributes } from 'react';
import { useInViewEffect } from 'react-hook-inview';

import cn from '@/helper/cn';

type SummarySectionType = HTMLAttributes<HTMLDivElement>;

const SummarySection = ({
  className,
  children,
  ...props
}: SummarySectionType) => {
  const sectionRef = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);

        anime
          .timeline({ loop: false })
          .add(
            {
              targets: '#summary-section h2', // NOTE: This animation assumes that there is an h2 tag inside the children
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 1000,
              easing: 'easeOutExpo',
            },
            '-=100',
          )
          .add(
            {
              targets: '#summary-section p', // NOTE: This animation assumes that there is a p tag inside the children
              opacity: [0, 1],
              translateY: [100, 0],
              duration: 750,
              easing: 'easeOutExpo',
            },
            '-=750',
          )
          .add(
            {
              targets: '#summary-demo-request-button',
              scale: [0.9, 1],
              opacity: [0, 1],
              duration: 150,
              easing: 'easeOutQuad',
            },
            '-=600',
          );
      }
    },
    { threshold: 0.2 },
  );

  return (
    <section
      ref={sectionRef}
      id="summary-section"
      className={cn('w-full bg-brand-50', className)}
      {...props}
    >
      <div className=" mx-auto flex h-full max-w-container flex-col items-center justify-center gap-6 px-10 py-24 text-center lg:gap-16 lg:px-20 lg:py-44">
        <div className="flex max-w-4xl flex-col gap-7">{children}</div>
        <Link
          id="summary-demo-request-button"
          href="/contact-us"
          className="max-w-[122.5px] rounded-full bg-brand-600 px-3 py-[.33rem] text-center text-xs font-medium text-slate-50 duration-200 hover:bg-brand-500 focus:bg-brand-500 active:bg-brand-700 sm:max-w-[150px] sm:text-base md:flex md:justify-center lg:max-w-[200px] lg:px-8 lg:py-4 lg:text-base"
        >
          Request a Demo
        </Link>
      </div>
    </section>
  );
};

export default SummarySection;
